import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SupportBadgeService } from './support-badge.service';

/**
 * Estado do painel "Ajuda e suporte" (spec visual §6.1). Compartilhado por
 * injeção: o gatilho no cabeçalho (`HeaderComponent`) e qualquer tela que
 * precise abrir um artigo específico (chamado, novidades) chamam os mesmos
 * métodos; quem RENDERIZA o painel (`SupportHubComponent`, montado uma vez
 * dentro do cabeçalho, só no tenant) lê os mesmos signals.
 *
 * Fica em `tenant/support/services` (não em `shared/components/support/`,
 * que é o kit de F3) porque esta tarefa (2A) não altera arquivos do kit —
 * ver `desvios_do_brief` do relatório desta etapa.
 */
@Injectable({ providedIn: 'root' })
export class SupportHubService {
  private readonly badge = inject(SupportBadgeService);

  private readonly _aberto = signal(false);
  private readonly _artigoAlvo = signal<string | null>(null);

  readonly aberto = this._aberto.asReadonly();
  readonly artigoAlvo = this._artigoAlvo.asReadonly();

  private readonly chamadosNaoLidosSignal = toSignal(this.badge.count$, { initialValue: 0 });
  private readonly novidadesNaoVistasSignal = toSignal(this.badge.novidades$, { initialValue: 0 });

  /** Chamados com resposta nova + versões não vistas (badge do cabeçalho). */
  readonly contagem = computed(() => this.chamadosNaoLidosSignal() + this.novidadesNaoVistasSignal());

  readonly chamadosNaoLidos = computed(() => this.chamadosNaoLidosSignal());
  readonly novidadesNaoVistas = computed(() => this.novidadesNaoVistasSignal());

  abrir(): void {
    this._aberto.set(true);
  }

  fechar(): void {
    this._aberto.set(false);
    this._artigoAlvo.set(null);
  }

  /** Chamado por "Artigos relacionados" (chamado) e "Ver artigo" (novidades): abre o painel já no artigo. */
  abrirArtigo(slug: string): void {
    this._artigoAlvo.set(slug);
    this._aberto.set(true);
  }
}
